import { Link, useLocation } from "react-router";
import { Shield, FileText } from "lucide-react";
import deskreplayLogo from "../../../deskreplay.png";
import { footerColumns } from "../data/footerLinks";
import { CookiePreferencesLink } from "./CookieConsent";

const FOOTER_CTA_HIDDEN_PATHS = ["/features", "/contact", "/request-demo", "/pricing", "/get-started"];
const FOOTER_MAIN_HIDDEN_PATHS = ["/get-started"];

export function Footer() {
  const { pathname } = useLocation();
  const showCta =
    !FOOTER_CTA_HIDDEN_PATHS.includes(pathname) &&
    !pathname.startsWith("/solutions") &&
    !pathname.startsWith("/industries") &&
    !pathname.startsWith("/platform") &&
    !pathname.startsWith("/legal");
  const showMain = !FOOTER_MAIN_HIDDEN_PATHS.includes(pathname);

  return (
    <footer className="bg-slate-950">
      <div
        className="h-px w-full bg-gradient-to-r from-transparent via-slate-400/25 to-transparent"
        aria-hidden
      />
      <div className="border-t border-slate-500/20">
        {showCta && (
          <div className="border-b border-teal-500/10 py-14 lg:py-16">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="space-y-6 max-w-xl">
                <h2 className="text-slate-100 text-2xl lg:text-3xl font-bold leading-tight">
                  Ready to establish an indisputable{" "}
                  <span className="text-teal-400">system of record?</span>
                </h2>
                <p className="text-on-dark-muted text-base leading-relaxed">
                  From the free 7-day pilot (no credit card) to PRO SaaS and Enterprise bring-your-own IdP SSO with
                  self-hosted deployment.
                  Compare tiers on the{" "}
                  <Link to="/pricing" className="text-teal-400 hover:text-teal-300">
                    pricing page
                  </Link>
                  .
                </p>
                <div className="flex flex-col sm:flex-row gap-3 pt-1">
                  <Link
                    to="/contact"
                    className="flex items-center justify-center gap-2 bg-teal-600 hover:bg-teal-500 text-white px-6 py-3 rounded-lg transition-all duration-200 hover:shadow-lg hover:shadow-teal-500/25 font-semibold text-sm"
                  >
                    <Shield size={16} />
                    Contact sales
                  </Link>
                  <Link
                    to="/resources/ciso-guide-non-invasive-forensics"
                    className="flex items-center justify-center gap-2 border border-teal-500/30 text-teal-400 hover:bg-teal-500/10 px-6 py-3 rounded-lg transition-all duration-200 font-medium text-sm"
                  >
                    <FileText size={16} />
                    Read CISO guide
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {showMain && (
        <div className="py-14 lg:py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-7 gap-x-6 gap-y-12 xl:gap-x-10">
              <div className="col-span-2 md:col-span-3 xl:col-span-2">
                {/* The logo wordmark is near-white, so it sits on a dark chip on the light footer. */}
                <div className="theme-dark mb-4 inline-flex items-center rounded-lg bg-slate-950 px-3.5 py-2.5">
                  <img src={deskreplayLogo} alt="DeskReplay" className="h-7 w-auto object-contain" />
                </div>
                <p className="text-on-dark-muted text-sm leading-relaxed mb-5">
                  Policy-driven visual session intelligence for enterprise workflows. Hosted SaaS with PRO, or Enterprise with
                  bring-your-own IdP SSO and optional self-hosted deployment.
                </p>
                <div className="flex flex-wrap gap-2 mb-2">
                  {["GDPR-oriented design", "HIPAA mappings", "SOC 2 style controls", "DPDP-oriented controls"].map((t) => (
                    <span
                      key={t}
                      className="px-2.5 py-1 rounded-md border border-teal-500/25 text-teal-400/90 text-xs font-medium"
                    >
                      {t}
                    </span>
                  ))}
                </div>
                <p className="text-slate-500 text-[11px] leading-snug max-w-sm mb-4">
                  References to SOC 2, GDPR, and DPDP describe our software&apos;s design goals and control capabilities, not
                  third-party certifications.
                </p>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                  <span className="text-emerald-400 text-xs font-medium">Services operational</span>
                </div>
              </div>

              {footerColumns.map((col) => (
                <div key={col.title} className="min-w-0 xl:max-w-none">
                  <p className="text-slate-100 mb-4 text-xs uppercase tracking-widest font-semibold">{col.title}</p>
                  <ul className="flex flex-col gap-2.5">
                    {col.links.map((link) => (
                      <li key={link.label + link.href}>
                        {link.external ? (
                          <a
                            href={link.href}
                            className="text-on-dark-muted hover:text-teal-400 text-sm leading-snug transition-colors block"
                          >
                            {link.label}
                          </a>
                        ) : (
                          <Link
                            to={link.href}
                            className="text-on-dark-muted hover:text-teal-400 text-sm leading-snug transition-colors block"
                          >
                            {link.label}
                          </Link>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className={`${showMain ? "border-t border-slate-800" : ""} py-6`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col lg:flex-row items-center justify-between gap-4">
          <p className="text-on-dark-muted text-xs text-center lg:text-left">
            © {new Date().getFullYear()} DeskReplay. All rights reserved.
          </p>
          <nav className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-xs">
            <Link to="/legal/privacy-policy" className="text-on-dark-muted hover:text-teal-400 transition-colors">
              Privacy policy
            </Link>
            <Link
              to="/legal/privacy-policy#us-residents"
              className="text-on-dark-muted hover:text-teal-400 transition-colors"
            >
              Your privacy rights
            </Link>
            <Link to="/legal/cookie-policy" className="text-on-dark-muted hover:text-teal-400 transition-colors">
              Cookie policy
            </Link>
            <CookiePreferencesLink />
            <Link to="/legal/terms-of-service" className="text-on-dark-muted hover:text-teal-400 transition-colors">
              Terms of service
            </Link>
            <Link to="/legal/disclaimer" className="text-on-dark-muted hover:text-teal-400 transition-colors">
              Disclaimer
            </Link>
            <Link to="/contact" className="text-on-dark-muted hover:text-teal-400 transition-colors">
              Contact
            </Link>
            <Link to="/faq" className="text-on-dark-muted hover:text-teal-400 transition-colors">
              FAQs
            </Link>
            <Link to="/resources" className="text-on-dark-muted hover:text-teal-400 transition-colors">
              Resources
            </Link>
            <a href="/sitemap.xml" className="text-on-dark-muted hover:text-teal-400 transition-colors">
              Sitemap
            </a>
            <a href="/llms.txt" className="text-on-dark-muted hover:text-teal-400 transition-colors">
              llms.txt
            </a>
            <a href="/LLM.txt" className="text-on-dark-muted hover:text-teal-400 transition-colors">
              LLM.txt
            </a>
          </nav>
        </div>
      </div>
    </footer>
  );
}
